import { ok, resolvedPromiseLike } from '@conduit-client/utils';
import { CacheInclusionPolicyService } from './cache-inclusion-policy';
import { queryToPredicate } from './cache-query';
import { buildUpdate } from './cache-update';
import type { CacheUpdate } from './cache-update';
import type { CacheQuery } from './cache-query';
import type { CacheEntry, Key, NamedCacheService, Cache } from '@conduit-client/service-cache/v1';
import type { CacheInclusionPolicyServiceDescriptor } from './cache-inclusion-policy';
import type { DeepReadonly, Result, SyncOrAsync } from '@conduit-client/utils';

/**
 * Implementation of CacheInclusionPolicy that uses a single level, in memory,
 * synchronous L1 cache.
 */
export class InMemoryCacheInclusionPolicy extends CacheInclusionPolicyService {
    constructor(protected services: NamedCacheService) {
        super();
    }

    /**
     * Reads data out of a single level in memory store.
     */
    read<D extends Result<void, unknown>>(options: {
        l1: Cache;
        readFromL1: (l1: Cache) => SyncOrAsync<D>;
    }): SyncOrAsync<D> {
        const { l1, readFromL1 } = options;
        // l1 is all we've got
        return readFromL1(l1);
    }

    /**
     * Writes data to a single level in memory store.
     */
    write<D extends Result<void, unknown>>(options: {
        l1: Cache;
        writeToL1: (l1: Cache) => SyncOrAsync<D>;
    }) {
        const { l1, writeToL1 } = options;
        return writeToL1(l1);
    }

    /**
     * Finds cache entries that match the given query.
     * Returns an async generator that yields `[key, entry]`.
     */
    async *find(
        query: CacheQuery
    ): AsyncGenerator<[key: Key, value: DeepReadonly<CacheEntry<unknown>>], void, unknown> {
        const cache = this.services.cache;
        const predicate = queryToPredicate(query);

        const filteredEntries = cache.filter(predicate).entries();
        for (const entry of filteredEntries) {
            yield entry;
        }
    }

    /**
     * Finds and modifies cache entries that match the given query.
     * Extends `find(query)` and returns an async generator of modified keys.
     */
    async *findAndModify(
        query: CacheQuery,
        cacheUpdate: CacheUpdate
    ): AsyncGenerator<Key, void, unknown> {
        const cache = this.services.cache;
        for await (const [key, value] of this.find(query)) {
            const update = buildUpdate(cacheUpdate, value);
            switch (update.type) {
                case 'entry':
                    this.write({
                        l1: cache,
                        writeToL1: (l1) => resolvedPromiseLike(ok(l1.set(key, update.entry))),
                    });
                    yield key;
                    break;
                case 'metadata':
                    this.write({
                        l1: cache,
                        writeToL1: (l1) =>
                            resolvedPromiseLike(ok(l1.setMetadata(key, update.metadata))),
                    });
                    yield key;
                    break;
                case 'delete':
                    this.write({
                        l1: cache,
                        writeToL1: (l1) => resolvedPromiseLike(ok(l1.delete(key))),
                    });
                    yield key;
                    break;
                case 'no-op':
            }
        }
    }
}

/**
 * Constructs an in-memory-only CacheInclusionPolicy.
 *
 * @returns in-memory-only CacheInclusionPolicy
 */
export function buildInMemoryCacheInclusionPolicyService(
    cache: Cache
): CacheInclusionPolicyServiceDescriptor {
    return {
        service: new InMemoryCacheInclusionPolicy({ cache }),
        type: 'cacheInclusionPolicy',
        version: '1.0',
    };
}
